// @givmo/board — EVERY word officer roles LAYER 2 adds, in one place: the board's own titles (the
// Officers part of Governance), the board's dates for each title, and the capacity a minutes signature
// is given in (CBG-YR3-ROLES layer 2, D-CBG-21).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// All of them. Jonathan approved the layer-2 frames for building on 2026-10-07 ("Approved") with
// every word kept a draft, and then the words themselves in copy round CBG-YR3-ROLES-COPY2 on
// 2026-10-12 13:49 PDT ("otherwise approved"), as written, "No title" included. Each line names the
// frame that draws it (`l2-…`), or "not drawn" for a state no frame shows.
//
// ⚠ Plan §3.2 and §3.4 bind every line: a title is a label the organization chose, and nothing here
// says what a title may do or what the law asks of it; a signature's capacity is what the person chose
// to record, never "certified". No pronoun for a named person.

export const officerRolesL2Words = {
  // ── Governance › Officers ──────────────────────────────────────────────────────────────────────

  // Drawn, l2-officers (the tab).
  officersTab: 'Officers',
  // Drawn, l2-officers.
  officersLead:
    'Your board’s officer titles. Rename them to match your bylaws, put them in your order, add your own, or retire one you don’t use.',
  // Drawn, l2-officers (the button; its dialog is not drawn).
  addTitle: 'Add a title',
  // Drawn, l2-officers (the card's heading).
  titlesCard: 'Titles',
  // Drawn, l2-officers ("Givmo’s “Chair”, renamed").
  renamedFrom: (label: string) => `Givmo’s “${label}”, renamed`,
  // Drawn, l2-officers ("Past President").
  customTitle: 'Your board’s own',
  // Drawn, l2-officers-rename (Vice Chair's row).
  nobodyHolds: 'Nobody holds it',
  // Drawn, l2-officers (the section's label).
  retired: 'Retired',
  // Drawn, l2-officers ("Retired Oct 5, 2026").
  retiredOn: (date: string) => `Retired ${date}`,
  // Drawn, l2-officers (each row's "⋯").
  rename: 'Rename',
  moveUp: 'Move up',
  moveDown: 'Move down',
  retire: 'Retire',
  // Not drawn: the row menu's accessible name.
  actionsFor: (label: string) => `Actions for ${label}`,
  // Drawn, l2-officers ("How this works", its three lines).
  titlesHow: [
    'Who holds a title is set on each member’s page.',
    'Renaming a title renames it everywhere it’s shown. A signature already recorded keeps the words it was made with.',
    'A title someone has held is never deleted. Retire it, and it stays in their history.',
  ],
  // Drawn, l2-officers-rename.
  renameTitle: (label: string) => `Rename “${label}”`,
  // Drawn, l2-officers-rename (the field's label; the add dialog reuses it, not drawn).
  titleField: 'Title',
  // Drawn, l2-officers-rename.
  renameNote:
    'Everyone who holds it keeps it, under the new name. A signature already recorded keeps the words it was made with.',
  // Drawn, l2-officers-rename.
  renameButton: 'Rename',
  // Not drawn: the add dialog, in the rename dialog's shape.
  addTitleNote: 'Your board’s own title. It goes last in the list, and you can move it.',
  addButton: 'Add it',
  // Not drawn: a retirement is not undone, so it asks first.
  retireTitle: (label: string) => `Retire “${label}”?`,
  retireBody:
    'It won’t be given to anyone new. Everyone who held it keeps it in their history, under the words it has today.',
  retireButton: 'Retire it',
  // Not drawn: toasts after each act.
  titleAdded: 'Title added.',
  titleRenamed: 'Title renamed.',
  titleMoved: 'Title moved.',
  titleRetired: 'Title retired.',
  // Not drawn: an Officers act that failed with no reason served.
  officersFailed: 'We couldn’t save that. Please try again.',
  // Not drawn: the titles couldn't be read.
  officersLoadFailed: 'We couldn’t load your board’s titles.',

  // ── The board's own dates for each title ───────────────────────────────────────────────────────

  // Drawn, l2-member-edit-dates (each ticked title's two fields).
  heldFrom: 'Held from',
  heldTo: 'Held to',
  // Drawn, l2-member-edit-dates.
  titleDatesNote: 'Your board’s own dates for each title. Leave them blank if you don’t have them.',
  // Drawn, l2-member-dates ("Since Oct 2, 2024").
  heldSince: (date: string) => `Since ${date}`,
  // Drawn, l2-member-dates ("Feb 1, 2023 – Oct 1, 2024").
  heldRange: (from: string, to: string) => `${from} – ${to}`,
  // Not drawn: an end date with no start.
  heldUntil: (date: string) => `Until ${date}`,
  // Not drawn: an earlier title with no dates recorded.
  heldNoDates: 'No dates recorded',
  // Drawn, l2-member-dates (the row's label).
  earlierTitles: 'Earlier titles',

  // ── The capacity a minutes signature is given in ───────────────────────────────────────────────

  // Drawn, l2-director-sign and l2-onbehalf.
  signingAs: 'Signing as',
  // Drawn, l2-director-sign (the empty choice: question 9).
  chooseCapacity: 'Choose a title',
  // Not drawn: the choice that takes a chosen title back off, so a signature can still go without
  // one (question 9's empty field, once something was chosen).
  withoutTitle: 'No title',
  // Drawn, l2-director-sign and l2-onbehalf.
  signingAsNote: 'Recorded with the signature, in the words it has today.',
  // Drawn, l2-signatures ("Maren Lindqvist, as Secretary").
  signedAs: (name: string, capacity: string) => `${name}, as ${capacity}`,
  // Not drawn: the backend refused a capacity the person no longer holds
  // (`board_signature_capacity_not_held`); the director's own sheet says it in their voice.
  capacityNotHeld:
    'That title isn’t on the roster for you any more, so it can’t be recorded with your signature. Choose another, or sign without one.',
} as const;
